<template>
  <div class="employee-dashboard" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <!-- Main Content -->
    <div class="dashboard-content">
      <div class="page-container">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="loader-visual">
            <div class="spinner"></div>
            <div class="pulse-ring"></div>
          </div>
          <p class="loading-text">Preparing your workspace...</p>
        </div>

        <div v-else-if="loadError" class="dashboard-error-state">
          <i class="bx bx-error-circle"></i>
          <h3>Could not load your dashboard</h3>
          <p>{{ loadError }}</p>
          <p v-if="apiHint" class="dashboard-error-hint">{{ apiHint }}</p>
          <button type="button" class="action-btn refresh-btn" @click="refreshDashboard">
            <i class="bx bx-refresh"></i> Try again
          </button>
        </div>

        <template v-else-if="dashboardData">
          <!-- Modern page header -->
          <div class="modern-page-header">
            <div class="header-overlay-glow"></div>
            <div class="header-content">
              <div class="title-section">
                <h1 class="dashboard-title">Welcome back, <span class="gradient-text">{{ dashboardData.employee.firstName }}</span>! 👋</h1>
                <p class="dashboard-subtitle">
                  <span class="sub-item">
                    <i class="bx bx-calendar"></i>
                    {{ getCurrentDate() }}
                  </span>
                  <span class="sub-separator">•</span>
                  <span class="sub-item">
                    <i class="bx bx-building-house"></i>
                    {{ dashboardData.employee.department }}
                  </span>
                </p>
              </div>
              <div class="header-actions">
                <button class="action-btn refresh-btn" @click="refreshDashboard" :disabled="refreshing">
                  <i class="bx bx-refresh" :class="{ 'spin-anim': refreshing }"></i>
                  <span>{{ refreshing ? 'Refreshing...' : 'Refresh' }}</span>
                </button>
                <button class="action-btn profile-btn" @click="gotTo('employee-profile')">
                  <i class="bx bx-user"></i>
                  <span>My Profile</span>
                </button>
              </div>
            </div>
            
            <div class="breadcrumb-section">
              <div class="breadcrumb">
                <span class="breadcrumb-item" @click="gotTo('dashboard')">
                  <i class="bx bx-home-alt"></i>
                  <span class="text">Home</span>
                </span>
                <span class="breadcrumb-separator">/</span>
                <span class="breadcrumb-item" @click="gotTo('employee-portal')">
                  <span class="text">Employee Portal</span>
                </span>
                <span class="breadcrumb-separator">/</span>
                <span class="breadcrumb-current">My Dashboard</span>
              </div>
            </div>
          </div>

          <!-- Today's Status Card -->
          <div class="status-card" :class="{ 'clocked-in': dashboardData.todayStatus.clockedIn && !dashboardData.todayStatus.clockedOut, 'clocked-out': dashboardData.todayStatus.clockedOut, 'not-clocked': !dashboardData.todayStatus.clockedIn }">
            <div class="status-card-glow"></div>
            <div class="status-header">
              <div class="status-info">
                <h3 class="status-title">
                  <i class="bx bx-time-five"></i>
                  Today's Attendance Status — {{ dashboardData.todayStatus.dayOfWeek }}
                </h3>
                <div class="status-badges">
                  <!-- Clocked In Status -->
                  <div v-if="dashboardData.todayStatus.clockedIn" class="status-badge badge-success">
                    <span class="pulse-dot green"></span>
                    <span>Clocked In: <strong>{{ dashboardData.todayStatus.attendance.checkInTime }}</strong></span>
                  </div>
                  <div v-else class="status-badge badge-warning">
                    <span class="pulse-dot orange"></span>
                    <span>Not Clocked In Yet</span>
                  </div>

                  <!-- Clocked Out Status -->
                  <div v-if="dashboardData.todayStatus.clockedOut" class="status-badge badge-info">
                    <span class="pulse-dot blue"></span>
                    <span>Clocked Out: <strong>{{ dashboardData.todayStatus.attendance.checkOutTime }}</strong></span>
                  </div>
                </div>
              </div>
              <div class="status-actions">
                <button v-if="!dashboardData.todayStatus.clockedIn"
                        @click="clockIn"
                        class="clock-action-btn btn-clock-in">
                  <i class="bx bx-log-in-circle"></i>
                  <span>Clock In</span>
                </button>
                <button v-else-if="!dashboardData.todayStatus.clockedOut"
                        @click="clockOut"
                        class="clock-action-btn btn-clock-out">
                  <i class="bx bx-log-out-circle"></i>
                  <span>Clock Out</span>
                </button>
                <button v-else class="clock-action-btn btn-completed" disabled>
                  <i class="bx bx-check-double"></i>
                  <span>Day Completed</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Quick Stats Cards -->
          <div class="metrics-grid">
            <!-- Attendance Rate -->
            <div class="metric-card card-indigo" @click="gotTo('employee-attendance-records')">
              <div class="card-glow-effect"></div>
              <div class="metric-icon">
                <i class="bx bx-calendar-check"></i>
              </div>
              <div class="metric-content">
                <p class="metric-label">Attendance Rate</p>
                <h2 class="metric-value">{{ dashboardData.quickStats.attendance.rate }}%</h2>
                <div class="metric-progress-wrapper">
                  <div class="metric-progress-bar">
                    <div class="progress-fill fill-indigo" :style="{ width: dashboardData.quickStats.attendance.rate + '%' }"></div>
                  </div>
                </div>
                <div class="metric-footer">
                  <span class="metric-detail">
                    <i class="bx bx-calendar-alt"></i> {{ dashboardData.quickStats.attendance.presentDays }} of {{ dashboardData.quickStats.attendance.workingDays }} days
                  </span>
                </div>
              </div>
            </div>

            <!-- Working Hours -->
            <div class="metric-card card-emerald" @click="gotTo('employee-attendance-records')">
              <div class="card-glow-effect"></div>
              <div class="metric-icon">
                <i class="bx bx-time"></i>
              </div>
              <div class="metric-content">
                <p class="metric-label">Hours This Month</p>
                <h2 class="metric-value">{{ dashboardData.quickStats.attendance.totalHours }}h</h2>
                <div class="metric-progress-wrapper no-bar"></div>
                <div class="metric-footer">
                  <span class="metric-detail">
                    <i class="bx bx-line-chart"></i> Avg: {{ dashboardData.quickStats.attendance.avgHoursPerDay }}h / day
                  </span>
                </div>
              </div>
            </div>

            <!-- Pending Actions -->
            <div class="metric-card card-rose" @click="gotTo('employee-documents-view')">
              <div class="card-glow-effect"></div>
              <div class="metric-icon">
                <i class="bx bx-bell"></i>
              </div>
              <div class="metric-content">
                <p class="metric-label">Pending Actions</p>
                <h2 class="metric-value">{{ getTotalAlerts() }}</h2>
                <div class="metric-progress-wrapper no-bar"></div>
                <div class="metric-footer">
                  <span class="metric-detail">
                    <i class="bx bx-error-alt"></i> {{ dashboardData.quickStats.documents.expiringSoon }} expiring documents
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Leave balances by type -->
          <section class="leave-balances-section">
            <div class="leave-balances-header">
              <h3 class="leave-balances-title">
                <i class="bx bx-plane-takeoff"></i>
                My Leave Balances
              </h3>
              <div class="leave-balances-meta">
                <span v-if="dashboardData.quickStats.leave.pendingRequests > 0" class="leave-pending-pill">
                  <span class="dot-blink"></span>
                  {{ dashboardData.quickStats.leave.pendingRequests }} Pending Requests
                </span>
                <button type="button" class="leave-view-btn" @click="gotTo('employee-leave-balance')">
                  <i class="bx bx-show"></i> View All
                </button>
                <button type="button" class="leave-apply-btn" @click="gotTo('employee-leave-apply')">
                  <i class="bx bx-plus"></i> Apply for Leave
                </button>
              </div>
            </div>
            
            <div v-if="leaveBalances.length === 0" class="leave-balances-empty">
              <i class="bx bx-info-circle"></i>
              <p>No leave balance information for this year.</p>
            </div>
            
            <div v-else class="leave-metrics-grid">
              <div
                v-for="balance in leaveBalances"
                :key="balance.leaveTypeName"
                class="leave-type-card"
                :class="getLeaveTypeClass(balance.leaveTypeName)"
                @click="gotTo('employee-leave-balance')"
              >
                <div class="leave-card-glow"></div>
                <div class="leave-card-header">
                  <div class="leave-icon-container">
                    <i :class="getLeaveIconName(balance.leaveTypeName)"></i>
                  </div>
                  <div class="leave-badge-right" :class="getRemainingBadgeClass(balance.remainingDays)">
                    {{ getRemainingText(balance.remainingDays) }}
                  </div>
                </div>
                
                <div class="leave-card-body">
                  <h4 class="leave-type-title">{{ balance.leaveTypeName }}</h4>
                  <div class="leave-days-display">
                    <span class="remaining-days">{{ formatLeaveDays(balance.remainingDays) }}</span>
                    <span class="days-label">days left</span>
                  </div>
                </div>
                
                <div class="leave-card-footer">
                  <div class="leave-mini-stats">
                    <span>Allocated: <strong>{{ formatLeaveDays(balance.totalDays) }}</strong></span>
                    <span class="stat-separator">•</span>
                    <span>Used: <strong>{{ formatLeaveDays(balance.usedDays) }}</strong></span>
                  </div>
                  
                  <div class="leave-progress-container">
                    <div class="leave-progress-bar">
                      <div class="progress-fill" :style="{ width: getLeavePercentage(balance) + '%' }"></div>
                    </div>
                    <span class="percentage-label">{{ getLeavePercentage(balance) }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Alerts Section -->
          <div v-if="dashboardData.alerts.expiringDocuments.length > 0" class="alert-section">
            <div class="alert-card warning">
              <div class="alert-header">
                <i class="bx bx-error-circle"></i>
                <strong>Action Required!</strong>
              </div>
              <p class="alert-message">
                You have {{ dashboardData.alerts.expiringDocuments.length }} document(s) expiring soon:
              </p>
              <ul class="alert-list">
                <li v-for="doc in dashboardData.alerts.expiringDocuments" :key="doc.id">
                  <div class="alert-list-item-content">
                    <i class="bx bx-file"></i>
                    <span><strong>{{ doc.name }}</strong> expires on {{ formatDate(doc.validTill) }} ({{ doc.daysUntilExpiry }} days remaining)</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Main Content Grid -->
          <div class="content-grid">
            <!-- Recent Attendance -->
            <div class="content-card">
              <div class="card-header">
                <div class="header-title-wrapper">
                  <h4 class="card-title">
                    <i class="bx bx-calendar-clock"></i>
                    Recent Attendance
                  </h4>
                </div>
                <button class="view-all-btn" @click="gotTo('employee-attendance-records')">
                  <span>View All</span> <i class="bx bx-chevron-right"></i>
                </button>
              </div>
              <div class="card-body">
                <div v-if="dashboardData.recentActivity.attendance.length === 0" class="empty-state">
                  <i class="bx bx-calendar-x"></i>
                  <p>No recent attendance records</p>
                </div>
                <div v-else class="table-container">
                  <table class="premium-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Check In</th>
                        <th>Check Out</th>
                        <th>Hours</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="record in dashboardData.recentActivity.attendance" :key="record.date">
                        <td class="date-cell">{{ formatDate(record.date) }}</td>
                        <td>
                          <span v-if="record.checkInTime" class="badge-time in">
                            <i class="bx bx-log-in"></i> {{ record.checkInTime }}
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                          <span v-if="record.checkOutTime" class="badge-time out">
                            <i class="bx bx-log-out"></i> {{ record.checkOutTime }}
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td class="hours-cell">{{ record.hoursWorked || '0' }}h</td>
                        <td>
                          <span class="badge-status status-present">{{ record.status || 'Present' }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Recent Leave Requests -->
            <div class="content-card">
              <div class="card-header">
                <div class="header-title-wrapper">
                  <h4 class="card-title">
                    <i class="bx bx-plane-takeoff"></i>
                    Recent Leave Requests
                  </h4>
                </div>
                <button class="view-all-btn" @click="gotTo('employee-leave-history')">
                  <span>View All</span> <i class="bx bx-chevron-right"></i>
                </button>
              </div>
              <div class="card-body">
                <div v-if="dashboardData.recentActivity.leaves.length === 0" class="empty-state">
                  <i class="bx bx-plane-off"></i>
                  <p>No recent leave requests</p>
                </div>
                <div v-else class="table-container">
                  <table class="premium-table">
                    <thead>
                      <tr>
                        <th>Leave Type</th>
                        <th>Period</th>
                        <th>Days</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="leave in dashboardData.recentActivity.leaves" :key="leave.id">
                        <td class="type-cell">{{ leave.leaveTypeName }}</td>
                        <td>
                          <div class="range-display">
                            <span class="range-date">{{ formatDate(leave.startDate) }}</span>
                            <span class="range-arrow"><i class="bx bx-right-arrow-alt"></i></span>
                            <span class="range-date">{{ formatDate(leave.endDate) }}</span>
                          </div>
                        </td>
                        <td class="days-cell">{{ leave.totalDays }}</td>
                        <td>
                          <span class="badge-status" :class="getLeaveStatusBadgeClass(leave.status)">
                            {{ leave.status }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Upcoming Holidays -->
            <div class="content-card">
              <div class="card-header">
                <div class="header-title-wrapper">
                  <h4 class="card-title">
                    <i class="bx bx-calendar-star"></i>
                    Upcoming Holidays
                  </h4>
                </div>
              </div>
              <div class="card-body">
                <div v-if="dashboardData.upcoming.holidays.length === 0" class="empty-state">
                  <i class="bx bx-calendar"></i>
                  <p>No upcoming holidays</p>
                </div>
                <div v-else class="list-wrapper">
                  <div v-for="holiday in dashboardData.upcoming.holidays"
                       :key="holiday.id"
                       class="list-item-card">
                    <div class="item-visual visual-holiday">
                      <i class="bx bx-calendar-event"></i>
                    </div>
                    <div class="item-details">
                      <h6 class="item-title">{{ holiday.name }}</h6>
                      <p class="item-date">
                        <i class="bx bx-time-five"></i>
                        {{ formatDate(holiday.date) }}
                      </p>
                    </div>
                    <div class="item-badge-wrapper">
                      <span class="item-badge badge-holiday">{{ holiday.type }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Announcements -->
            <div class="content-card">
              <div class="card-header">
                <div class="header-title-wrapper">
                  <h4 class="card-title">
                    <i class="bx bx-bullhorn"></i>
                    Latest Announcements
                  </h4>
                </div>
                <button class="view-all-btn" @click="gotTo('employee-announcements')">
                  <span>View All</span> <i class="bx bx-chevron-right"></i>
                </button>
              </div>
              <div class="card-body">
                <div v-if="dashboardData.announcements.length === 0" class="empty-state">
                  <i class="bx bx-bullhorn-outline"></i>
                  <p>No announcements</p>
                </div>
                <div v-else class="list-wrapper">
                  <div v-for="announcement in dashboardData.announcements"
                       :key="announcement.id"
                       class="list-item-card">
                    <div class="item-visual" :class="getPriorityClass(announcement.priority)">
                      <i class="bx bx-bell"></i>
                    </div>
                    <div class="item-details">
                      <h6 class="item-title">{{ announcement.title }}</h6>
                      <p class="item-desc">
                        {{ truncateText(announcement.content, 90) }}
                      </p>
                      <p class="item-date">
                        <i class="bx bx-time-five"></i>
                        {{ formatDate(announcement.publish_date) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Latest Payslip -->
          <div v-if="dashboardData.latestPayslip" class="payslip-card">
            <div class="payslip-accent-glow"></div>
            <div class="payslip-content">
              <div class="payslip-icon-wrapper">
                <i class="bx bx-wallet"></i>
              </div>
              <div class="payslip-details">
                <h3 class="payslip-title">Latest Payslip</h3>
                <p class="payslip-period">
                  Pay Period: <span>{{ formatDate(dashboardData.latestPayslip.payPeriodStart) }}</span> to <span>{{ formatDate(dashboardData.latestPayslip.payPeriodEnd) }}</span>
                </p>
                <div class="payslip-amount-container">
                  <span class="currency-label">KES</span>
                  <span class="amount-value">{{ formatCurrency(dashboardData.latestPayslip.netPay) }}</span>
                </div>
              </div>
              <div class="payslip-action-container">
                <button @click="gotTo('employee-payroll-payslips')" class="payslip-download-btn">
                  <i class="bx bx-cloud-download"></i>
                  <span>View & Download Payslip</span>
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation.vue";
import { authUrl, base, execute, parseApiErrorMessage } from "@/api";

export default {
  name: "EmployeeDashboard",
  components: { Navigation },

  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      refreshing: false,
      dashboardData: null,
      loadError: "",
      apiHint: "",
      realtimeClockStatus: null,
      clockProcessing: false
    };
  },

  computed: {
    leaveBalances() {
      return this.dashboardData?.quickStats?.leave?.balances ?? [];
    }
  },

  mounted() {
    this.loadDashboard();
  },

  methods: {
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },

    async loadDashboard() {
      this.loading = true;
      this.loadError = "";
      this.apiHint = "";

      try {
        const data = new FormData();
        data.append("function", "getEmployeeDashboard");

        const res = await execute(data, authUrl);

        if (res?.data?.success && res.data.data?.employee) {
          this.dashboardData = res.data.data;
          return;
        }

        const msg = res?.data?.message || "Failed to load dashboard";
        // Remote UAT may still return attendance-processor payload instead of dashboard data
        if (res?.data?.data?.processedCount != null && !res?.data?.data?.employee) {
          throw new Error(
            "API returned attendance processing data instead of dashboard. " +
            "Deploy the latest authentication/index.php to this server, or use local API (restart npm run serve)."
          );
        }
        throw new Error(msg);
      } catch (error) {
        const msg = parseApiErrorMessage(error, "Failed to load dashboard");
        this.loadError = msg;
        this.dashboardData = null;
        if (/localhost/i.test(base)) {
          this.apiHint = "Using API: " + base;
        } else {
          this.apiHint =
            "Using API: " + base +
            ". For local dev, stop the server, ensure VUE_APP_FORCE_REMOTE_API is not true, and run npm run serve again.";
        }
        console.error("Failed to load dashboard:", error);
      } finally {
        this.loading = false;
      }
    },

    async refreshDashboard() {
      this.refreshing = true;
      await this.loadDashboard();
      this.refreshing = false;
    },

    async clockIn() {
      await this.performClockAction("IN");
    },

    async clockOut() {
      await this.performClockAction("OUT");
    },

    async loadRealtimeClockStatus() {
      try {
        this.realtimeClockStatus = {
          isClockedIn: this.dashboardData?.todayStatus?.clockedIn,
          checkOutTime: this.dashboardData?.todayStatus?.attendance?.checkOutTime
        };
      } catch (e) {
        console.error("Failed to load real-time status", e);
      }
    },

    async performClockAction(action) {
      if (this.clockProcessing) return;
      this.clockProcessing = true;
      try {
        const intendedAction = action;
        const location = await this.getCurrentLocationForClock();
        const data = new FormData();
        data.append("function", "employeeClockAction");
        data.append("action", action);
        data.append("latitude", String(location.latitude));
        data.append("longitude", String(location.longitude));
        data.append("address", location.address);
        data.append("notes", "");

        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          if (this.$toast) this.$toast.success(res.data.message || (action === "IN" ? "Clock in successful" : "Clock out successful"));
          else alert(res?.data?.message || (action === "IN" ? "Clock in successful" : "Clock out successful"));
          await this.loadDashboard();
          await this.loadRealtimeClockStatus();
        } else {
          // Reconcile with latest dashboard state to avoid false negatives.
          await this.loadDashboard();
          await this.loadRealtimeClockStatus();
          const status = this.dashboardData?.todayStatus || {};
          const actionActuallyApplied =
            (intendedAction === "IN" && !!(this.realtimeClockStatus?.isClockedIn || status.clockedIn || status?.attendance?.checkInTime || status.checkInTime)) ||
            (intendedAction === "OUT" && !!(this.realtimeClockStatus?.checkOutTime || status.clockedOut || status?.attendance?.checkOutTime || status.checkOutTime));
          if (actionActuallyApplied) {
            if (this.$toast) this.$toast.success(res?.data?.message || "Attendance recorded.");
            else alert(res?.data?.message || "Attendance recorded.");
          } else {
            throw new Error(res?.data?.message || "Failed to record attendance.");
          }
        }
      } catch (error) {
        const msg = error?.message || "Could not complete clock action.";
        const locationIssue =
          msg.toLowerCase().includes("location") ||
          msg.toLowerCase().includes("geolocation") ||
          msg.toLowerCase().includes("permission");
        if (locationIssue) {
          const fallbackMsg = "Location is required to clock in/out from dashboard. Redirecting to Attendance Clock page.";
          if (this.$toast) this.$toast.warning(fallbackMsg);
          else alert(fallbackMsg);
          this.gotTo("employee-attendance-clock");
        } else {
          // Request may fail client-side after backend already saved; reconcile before showing error.
          await this.loadDashboard();
          await this.loadRealtimeClockStatus();
          const status = this.dashboardData?.todayStatus || {};
          const actionActuallyApplied =
            (action === "IN" && !!(this.realtimeClockStatus?.isClockedIn || status.clockedIn || status?.attendance?.checkInTime || status.checkInTime)) ||
            (action === "OUT" && !!(this.realtimeClockStatus?.checkOutTime || status.clockedOut || status?.attendance?.checkOutTime || status.checkOutTime));

          if (actionActuallyApplied) {
            const okMsg = action === "IN" ? "Clock in successful." : "Clock out successful.";
            if (this.$toast) this.$toast.success(okMsg);
            else alert(okMsg);
          } else {
            if (this.$toast) this.$toast.error(msg);
            else alert(msg);
          }
        }
      } finally {
        this.clockProcessing = false;
      }
    },

    getCurrentLocationForClock() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Location is not supported by your browser."));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            const latitude = pos.coords.latitude;
            const longitude = pos.coords.longitude;
            let address = `Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
            try {
              const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`, { headers: { Accept: "application/json" } });
              if (res.ok) {
                const data = await res.json();
                const a = data.address || {};
                const parts = [a.road, a.suburb, a.city, (a.county && !a.city) ? a.county : null, a.country].filter(Boolean);
                if (parts.length) address = parts.join(", ");
                else if (data.display_name) address = data.display_name;
              }
            } catch (_) {}
            resolve({ latitude, longitude, address });
          },
          (err) => {
            if (err.code === 1) reject(new Error("Location access denied. Please enable location permissions."));
            else if (err.code === 2) reject(new Error("Location unavailable. Please try again."));
            else if (err.code === 3) reject(new Error("Location request timed out. Please try again."));
            else reject(new Error("Failed to get location."));
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });
    },

    getCurrentDate() {
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return new Date().toLocaleDateString('en-US', options);
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatLeaveDays(value) {
      const n = parseFloat(value);
      if (Number.isNaN(n)) return "0";
      return Number.isInteger(n) ? String(n) : n.toFixed(1);
    },

    getTotalAlerts() {
      if (!this.dashboardData) return 0;
      return (
          this.dashboardData.quickStats.leave.pendingRequests +
          this.dashboardData.quickStats.imprest.pendingRequests +
          this.dashboardData.quickStats.documents.expiringSoon
      );
    },

    getLeavePercentage(balance) {
      if (!balance.totalDays || balance.totalDays == 0) return 0;
      return Math.round((balance.remainingDays / balance.totalDays) * 100);
    },

    getLeaveStatusClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'approved') return 'status-badge success';
      if (statusLower === 'rejected') return 'status-badge danger';
      if (statusLower === 'pending') return 'status-badge warning';
      return 'status-badge neutral';
    },

    getLeaveStatusBadgeClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'approved') return 'status-approved';
      if (statusLower === 'rejected') return 'status-rejected';
      if (statusLower === 'pending') return 'status-pending';
      return 'status-neutral';
    },

    getAnnouncementIconClass(priority) {
      const priorityLower = priority?.toLowerCase();
      if (priorityLower === 'high' || priorityLower === 'urgent') {
        return 'item-icon danger';
      }
      if (priorityLower === 'medium') {
        return 'item-icon warning';
      }
      return 'item-icon info';
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      const stripped = text.replace(/<[^>]*>/g, '');
      if (stripped.length <= maxLength) return stripped;
      return stripped.substring(0, maxLength) + '...';
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    },

    getLeaveTypeClass(name) {
      const lower = String(name).toLowerCase();
      if (lower.includes('annual')) return 'leave-annual';
      if (lower.includes('sick') || lower.includes('medical')) return 'leave-sick';
      if (lower.includes('parental') || lower.includes('maternity') || lower.includes('paternity')) return 'leave-parental';
      return 'leave-other';
    },

    getLeaveIconName(name) {
      const lower = String(name).toLowerCase();
      if (lower.includes('annual')) return 'bx bx-sun';
      if (lower.includes('sick') || lower.includes('medical')) return 'bx bx-first-aid';
      if (lower.includes('parental') || lower.includes('maternity') || lower.includes('paternity')) return 'bx bx-heart';
      if (lower.includes('compassionate')) return 'bx bx-donate-blood';
      if (lower.includes('study') || lower.includes('education')) return 'bx bx-book-open';
      return 'bx bx-calendar-star';
    },

    getRemainingBadgeClass(remaining) {
      if (remaining > 5) return 'status-good';
      if (remaining > 0) return 'status-low';
      return 'status-depleted';
    },

    getRemainingText(remaining) {
      if (remaining > 5) return 'Available';
      if (remaining > 0) return 'Low Balance';
      return 'Depleted';
    },

    getPriorityClass(priority) {
      const p = String(priority).toLowerCase();
      if (p === 'high' || p === 'urgent') return 'priority-high';
      if (p === 'medium') return 'priority-medium';
      return 'priority-low';
    }
  }
};
</script>

<style scoped>
/* ===== PREMIUM VIBRANT DESIGN SYSTEM FOR EMPLOYEE DASHBOARD ===== */
.employee-dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #1e293b;
  box-sizing: border-box;
}

.employee-dashboard.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

.dashboard-content {
  margin-left: 280px;
  padding: 30px 40px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 30px;
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* --- PREMIUM LOADING SKELETON & SPINNER --- */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 20px;
}

.dashboard-error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 12px;
  padding: 40px 24px;
  text-align: center;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #fecaca;
  box-shadow: 0 4px 24px rgba(239, 68, 68, 0.08);
}

.dashboard-error-state i.bx-error-circle {
  font-size: 48px;
  color: #ef4444;
}

.dashboard-error-state h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #991b1b;
}

.dashboard-error-state p {
  margin: 0;
  max-width: 520px;
  color: #64748b;
  line-height: 1.5;
}

.dashboard-error-hint {
  font-size: 0.875rem;
  color: #94a3b8 !important;
  word-break: break-all;
}

.dashboard-error-state .refresh-btn {
  margin-top: 8px;
}

.loader-visual {
  position: relative;
  width: 70px;
  height: 70px;
}

.spinner {
  width: 100%;
  height: 100%;
  border: 4px solid rgba(79, 70, 229, 0.1);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 1s cubic-bezier(0.55, 0.15, 0.45, 0.85) infinite;
}

.pulse-ring {
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border: 2px solid rgba(124, 58, 237, 0.2);
  border-radius: 50%;
  animation: pulse-ring-anim 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse-ring-anim {
  0% { transform: scale(0.95); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.5; }
  100% { transform: scale(1.2); opacity: 0; }
}

.loading-text {
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  letter-spacing: 0.5px;
}

/* --- MODERN HEADER CARD --- */
.modern-page-header {
  position: relative;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  border-radius: 24px;
  padding: 30px 40px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

.header-overlay-glow {
  position: absolute;
  top: -150px;
  right: -150px;
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, rgba(255, 255, 255, 0) 70%);
  z-index: 0;
  pointer-events: none;
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.dashboard-title {
  font-size: 32px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.5px;
}

.gradient-text {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.dashboard-subtitle {
  margin: 8px 0 0 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
}

.dashboard-subtitle i {
  font-size: 16px;
  color: #4f46e5;
}

.sub-separator {
  color: #cbd5e1;
}

.header-actions {
  display: flex;
  gap: 14px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
}

.refresh-btn {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.2);
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(79, 70, 229, 0.35);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.profile-btn {
  background: #ffffff;
  color: #475569;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
}

.profile-btn:hover {
  background: #f8fafc;
  color: #4f46e5;
  border-color: #cbd5e1;
  transform: translateY(-2px);
}

.spin-anim {
  animation: spin 1s linear infinite;
}

.breadcrumb-section {
  position: relative;
  z-index: 1;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: color 0.2s;
}

.breadcrumb-item:hover {
  color: #4f46e5;
}

.breadcrumb-separator {
  color: #cbd5e1;
}

.breadcrumb-current {
  color: #94a3b8;
}

/* --- CLOCK IN / OUT STATUS CARD --- */
.status-card {
  position: relative;
  border-radius: 24px;
  padding: 30px 40px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
}

.status-card.not-clocked {
  background: linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%);
  border-color: #fef3c7;
}

.status-card.clocked-in {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-color: #d1fae5;
}

.status-card.clocked-out {
  background: linear-gradient(135deg, #f0f9ff 0%, #eff6ff 100%);
  border-color: #e0f2fe;
}

.status-card-glow {
  position: absolute;
  bottom: -80px;
  right: -80px;
  width: 250px;
  height: 250px;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.15;
}

.not-clocked .status-card-glow { background: #f59e0b; }
.clocked-in .status-card-glow { background: #10b981; }
.clocked-out .status-card-glow { background: #3b82f6; }

.status-header {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.status-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
}

.status-title i {
  font-size: 22px;
  color: #4f46e5;
}

.status-badges {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
}

.badge-success {
  background: rgba(16, 185, 129, 0.12);
  color: #065f46;
}

.badge-warning {
  background: rgba(245, 158, 11, 0.12);
  color: #92400e;
}

.badge-info {
  background: rgba(59, 130, 246, 0.12);
  color: #1e40af;
}

/* Pulsing Status Dot indicator */
.pulse-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  position: relative;
}

.pulse-dot.green { background-color: #10b981; }
.pulse-dot.orange { background-color: #f59e0b; }
.pulse-dot.blue { background-color: #3b82f6; }

.pulse-dot::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  animation: pulse-ring-glow 1.5s infinite ease-in-out;
}

.pulse-dot.green::after { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.4); }
.pulse-dot.orange::after { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0.4); }
.pulse-dot.blue::after { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.4); }

@keyframes pulse-ring-glow {
  0% { transform: scale(0.8); opacity: 0.5; }
  100% { transform: scale(2.4); opacity: 0; }
}

.clock-action-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 32px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-clock-in {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.25);
}

.btn-clock-in:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 22px rgba(16, 185, 129, 0.4);
}

.btn-clock-out {
  background: linear-gradient(135deg, #f43f5e 0%, #e11d48 100%);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(244, 63, 94, 0.25);
}

.btn-clock-out:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 22px rgba(244, 63, 94, 0.4);
}

.btn-completed {
  background: rgba(148, 163, 184, 0.2);
  color: #64748b;
  border: 1px solid rgba(148, 163, 184, 0.3);
  cursor: not-allowed;
}

/* --- QUICK STATS / METRICS GRID --- */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
}

.metric-card {
  position: relative;
  background: #ffffff;
  border-radius: 24px;
  padding: 30px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
}

.metric-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.06);
}

.card-glow-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
}

.card-indigo .card-glow-effect { background: #4f46e5; }
.card-emerald .card-glow-effect { background: #10b981; }
.card-rose .card-glow-effect { background: #f43f5e; }

.metric-icon {
  position: absolute;
  top: 25px;
  right: 25px;
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
}

.metric-icon i {
  font-size: 24px;
}

.card-indigo .metric-icon i { color: #4f46e5; }
.card-emerald .metric-icon i { color: #10b981; }
.card-rose .metric-icon i { color: #f43f5e; }

.metric-content {
  padding-right: 60px;
}

.metric-label {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 10px 0;
}

.metric-value {
  font-size: 34px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 12px 0;
}

.metric-progress-wrapper {
  height: 6px;
  margin-bottom: 12px;
}

.metric-progress-wrapper.no-bar {
  height: 6px;
  margin-bottom: 12px;
}

.metric-progress-bar {
  height: 100%;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
}

.fill-indigo {
  background: linear-gradient(90deg, #4f46e5, #7c3aed);
}

.metric-footer {
  display: flex;
  align-items: center;
}

.metric-detail {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.metric-detail i {
  font-size: 15px;
}

/* --- LEAVE BALANCES SECTION --- */
.leave-balances-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.leave-balances-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.leave-balances-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.leave-balances-title i {
  color: #0ea5e9;
}

.leave-balances-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.leave-pending-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 20px;
  background: #fffbeb;
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #fef3c7;
}

.dot-blink {
  width: 8px;
  height: 8px;
  background-color: #f59e0b;
  border-radius: 50%;
  animation: blink 1.2s infinite ease-in-out;
}

@keyframes blink {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

.leave-view-btn,
.leave-apply-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.leave-view-btn {
  background: #ffffff;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.leave-view-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0ea5e9;
}

.leave-apply-btn {
  background: #0ea5e9;
  color: #ffffff;
  box-shadow: 0 4px 10px rgba(14, 165, 233, 0.15);
}

.leave-apply-btn:hover {
  background: #0284c7;
  box-shadow: 0 6px 15px rgba(14, 165, 233, 0.25);
  transform: translateY(-1px);
}

.leave-balances-empty {
  text-align: center;
  padding: 40px;
  border-radius: 20px;
  background: rgba(248, 250, 252, 0.6);
  border: 1px dashed #cbd5e1;
  color: #64748b;
}

.leave-balances-empty i {
  font-size: 32px;
  margin-bottom: 10px;
  color: #cbd5e1;
}

.leave-metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.leave-type-card {
  position: relative;
  background: #ffffff;
  border-radius: 20px;
  padding: 24px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.01);
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
}

.leave-type-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
}

.leave-card-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
}

.leave-annual .leave-card-glow { background: #3b82f6; }
.leave-sick .leave-card-glow { background: #10b981; }
.leave-parental .leave-card-glow { background: #f59e0b; }
.leave-other .leave-card-glow { background: #8b5cf6; }

.leave-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.leave-icon-container {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}

.leave-annual .leave-icon-container i { color: #3b82f6; }
.leave-sick .leave-icon-container i { color: #10b981; }
.leave-parental .leave-icon-container i { color: #f59e0b; }
.leave-other .leave-icon-container i { color: #8b5cf6; }

.leave-icon-container i {
  font-size: 20px;
}

.leave-badge-right {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-good {
  background: rgba(16, 185, 129, 0.1);
  color: #047857;
}

.status-low {
  background: rgba(245, 158, 11, 0.1);
  color: #b45309;
}

.status-depleted {
  background: rgba(239, 68, 68, 0.1);
  color: #b91c1c;
}

.leave-type-title {
  font-size: 15px;
  font-weight: 700;
  color: #475569;
  margin: 0 0 6px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leave-days-display {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.remaining-days {
  font-size: 32px;
  font-weight: 800;
  color: #0f172a;
}

.days-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.leave-card-footer {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.leave-mini-stats {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.leave-mini-stats strong {
  color: #334155;
}

.stat-separator {
  color: #e2e8f0;
  margin: 0 6px;
}

.leave-progress-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.leave-progress-bar {
  flex: 1;
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
}

.leave-annual .leave-progress-bar .progress-fill { background: #3b82f6; }
.leave-sick .leave-progress-bar .progress-fill { background: #10b981; }
.leave-parental .leave-progress-bar .progress-fill { background: #f59e0b; }
.leave-other .leave-progress-bar .progress-fill { background: #8b5cf6; }

.percentage-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  min-width: 28px;
  text-align: right;
}

/* --- WARNING/ALERT SECTION --- */
.alert-section {
  margin-bottom: 10px;
}

.alert-card {
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
}

.alert-card.warning {
  background: linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%);
  border: 1px solid #fef3c7;
}

.alert-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #b45309;
  margin-bottom: 10px;
}

.alert-header i {
  font-size: 20px;
}

.alert-message {
  color: #78350f;
  font-size: 13px;
  margin: 0 0 14px 0;
}

.alert-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.alert-list li {
  padding: 8px 0;
  border-bottom: 1px solid rgba(251, 191, 36, 0.15);
}

.alert-list li:last-child {
  border-bottom: none;
}

.alert-list-item-content {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #78350f;
}

.alert-list-item-content i {
  font-size: 16px;
  color: #d97706;
}

/* --- MAIN CONTENT CARD & TABLES --- */
.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(480px, 1fr));
  gap: 24px;
}

.content-card {
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.content-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
}

.card-header {
  padding: 22px 28px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title-wrapper {
  display: flex;
  align-items: center;
}

.card-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title i {
  font-size: 20px;
  color: #4f46e5;
}

.view-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.view-all-btn:hover {
  background: #f1f5f9;
  color: #4f46e5;
  border-color: #cbd5e1;
}

.card-body {
  padding: 24px 28px;
  flex: 1;
}

/* --- EMPTY STATES --- */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #94a3b8;
  text-align: center;
}

.empty-state i {
  font-size: 40px;
  margin-bottom: 12px;
  color: #cbd5e1;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

/* --- TABLES --- */
.table-container {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
}

.premium-table {
  width: 100%;
  border-collapse: collapse;
}

.premium-table th {
  padding: 14px 16px;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
}

.premium-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
  font-size: 13.5px;
}

.premium-table tbody tr {
  transition: background-color 0.2s ease;
}

.premium-table tbody tr:hover {
  background-color: #f8fafc;
}

.date-cell {
  font-weight: 600;
  color: #334155;
}

.hours-cell {
  font-weight: 600;
  color: #475569;
}

.type-cell {
  font-weight: 600;
  color: #4f46e5;
}

.badge-time {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
}

.badge-time.in {
  background: rgba(16, 185, 129, 0.08);
  color: #059669;
}

.badge-time.out {
  background: rgba(244, 63, 94, 0.08);
  color: #e11d48;
}

.range-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.range-date {
  font-size: 12.5px;
  color: #475569;
}

.range-arrow {
  color: #94a3b8;
  display: flex;
  align-items: center;
}

.badge-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-block;
}

.status-present {
  background: rgba(16, 185, 129, 0.08);
  color: #059669;
}

.status-approved {
  background: rgba(16, 185, 129, 0.08);
  color: #059669;
}

.status-rejected {
  background: rgba(244, 63, 94, 0.08);
  color: #e11d48;
}

.status-pending {
  background: rgba(245, 158, 11, 0.08);
  color: #d97706;
}

.status-neutral {
  background: rgba(100, 116, 139, 0.08);
  color: #475569;
}

/* --- LIST WRAPPER FOR HOLIDAYS & ANNOUNCEMENTS --- */
.list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.list-item-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.list-item-card:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateX(4px);
}

.item-visual {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.item-visual i {
  font-size: 20px;
}

.visual-holiday {
  background: rgba(245, 158, 11, 0.08);
  color: #d97706;
}

.priority-high {
  background: rgba(244, 63, 94, 0.08);
  color: #e11d48;
}

.priority-medium {
  background: rgba(245, 158, 11, 0.08);
  color: #d97706;
}

.priority-low {
  background: rgba(59, 130, 246, 0.08);
  color: #2563eb;
}

.item-details {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-size: 14.5px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-desc {
  font-size: 13px;
  color: #475569;
  margin: 0 0 6px 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-date {
  font-size: 12px;
  color: #64748b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.item-badge-wrapper {
  flex-shrink: 0;
}

.item-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  text-transform: uppercase;
}

.badge-holiday {
  background: rgba(79, 70, 229, 0.08);
  color: #4f46e5;
}

/* --- PREMIUM PAYSLIP CARD --- */
.payslip-card {
  position: relative;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border-radius: 24px;
  padding: 35px 40px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15);
}

.payslip-accent-glow {
  position: absolute;
  top: -150px;
  left: -100px;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, rgba(0, 0, 0, 0) 70%);
  z-index: 0;
  pointer-events: none;
}

.payslip-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 30px;
}

.payslip-icon-wrapper {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.payslip-icon-wrapper i {
  font-size: 28px;
  color: #10b981;
}

.payslip-details {
  flex: 1;
  min-width: 300px;
}

.payslip-title {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 6px 0;
}

.payslip-period {
  font-size: 13.5px;
  color: #94a3b8;
  margin: 0 0 16px 0;
}

.payslip-period span {
  color: #e2e8f0;
  font-weight: 600;
}

.payslip-amount-container {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.currency-label {
  font-size: 16px;
  font-weight: 700;
  color: #10b981;
}

.amount-value {
  font-size: 38px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.5px;
}

.payslip-action-container {
  display: flex;
  align-items: center;
}

.payslip-download-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 14px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.25s;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.2);
}

.payslip-download-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.35);
}

/* --- RESPONSIVE STYLING --- */
@media (max-width: 1200px) {
  .dashboard-content {
    padding: 24px 30px;
  }
}

@media (max-width: 1024px) {
  .dashboard-content {
    margin-left: 70px;
  }
  
  .employee-dashboard.nav-collapsed .dashboard-content {
    margin-left: 70px;
  }
  
  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    padding: 16px;
  }
  
  .employee-dashboard.nav-collapsed .dashboard-content {
    margin-left: 0;
  }
  
  .modern-page-header {
    padding: 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .action-btn {
    flex: 1;
    justify-content: center;
  }
  
  .status-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .status-actions {
    width: 100%;
  }
  
  .clock-action-btn {
    width: 100%;
    justify-content: center;
  }
  
  .payslip-content {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
  
  .payslip-icon-wrapper {
    margin: 0 auto;
  }
  
  .payslip-amount-container {
    justify-content: center;
  }
  
  .payslip-download-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .dashboard-title {
    font-size: 24px;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .leave-metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .amount-value {
    font-size: 32px;
  }
}

/* --- PRINT RULES --- */
@media print {
  .header-actions,
  .status-actions,
  .view-all-btn,
  .leave-balances-meta,
  .payslip-action-container {
    display: none !important;
  }
  
  .dashboard-content {
    margin-left: 0 !important;
    padding: 0 !important;
  }
  
  .modern-page-header,
  .status-card,
  .metric-card,
  .leave-type-card,
  .content-card,
  .payslip-card {
    border: 1px solid #cbd5e1 !important;
    background: #ffffff !important;
    box-shadow: none !important;
    color: #000000 !important;
    break-inside: avoid;
  }
  
  .gradient-text,
  .amount-value {
    -webkit-text-fill-color: initial !important;
    color: #000000 !important;
  }
}
</style>